import React, { useEffect, useState, useCallback, useRef } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { debounce } from "lodash";
import ic_search from "../../assets/images/icons/search.svg";
import { useTranslation } from "react-i18next";
import { useClickOutside } from "../../hooks";
import GeneralInput from "../Inputs/GeneralInput";
import Loader from "../Common/Loader";

interface SearchComponentProps {
  onSearch: (data: string) => void;
  value?: string;
  isLoading?: boolean;
  className?: string;
  inputStyle?: string;
  debounceDelay?: number;
  searchOn?: "click" | "typing";
  showSearchHistory?: boolean;
  placeholder?: string;
}

const RECENT_SEARCHES_KEY = "recentSearches";

const SearchComponent: React.FC<SearchComponentProps> = ({
  onSearch,
  value,
  isLoading,
  className,
  inputStyle,
  debounceDelay = 500,
  searchOn = "click",
  showSearchHistory = false,
  placeholder,
}) => {
  const [searchQuery, setSearchQuery] = useState(value || "");
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);

  const { t } = useTranslation();
  const methods = useForm({
    defaultValues: { search: value },
  });

  const { handleSubmit, setValue } = methods;
  const dropdownRef = useRef<HTMLDivElement>(null);

  useClickOutside(dropdownRef, () => setShowDropdown(false));

  useEffect(() => {
    setValue("search", searchQuery);
  }, [searchQuery, setValue, value]);

  useEffect(() => {
    setValue("search", value);
    setSearchQuery(value || "");
  }, [value]);

  useEffect(() => {
    const storedSearches = JSON.parse(
      localStorage.getItem(RECENT_SEARCHES_KEY) || "[]",
    );
    setRecentSearches(storedSearches);
  }, []);

  const debouncedSearch = useCallback(
    debounce((query: string) => {
      // Only call onSearch if query is not empty
      if (query.trim()) {
        onSearch(query);
      } else {
        // If empty, pass empty string to clear results
        onSearch("");
      }
    }, debounceDelay),
    [onSearch, debounceDelay],
  );

  const handleChange = (e: React.ChangeEvent<any>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowDropdown(true);

    if (searchOn === "typing") {
      debouncedSearch(value);
    }
  };

  const handleSearchOnClick = handleSubmit((data) => {
    const query = data?.search?.trim() ?? "";

    // Only proceed if query is not empty
    if (!query) {
      // Optionally clear results or show message
      onSearch("");
      setShowDropdown(false);
      return;
    }

    // Add to recent searches
    if (!recentSearches.includes(query)) {
      const updatedSearches = [query, ...recentSearches].slice(0, 5);
      setRecentSearches(updatedSearches);
      localStorage.setItem(
        RECENT_SEARCHES_KEY,
        JSON.stringify(updatedSearches),
      );
    }
    onSearch(query);
    setShowDropdown(false);
  });

  const removeSearch = (search: string) => {
    const updatedSearches = recentSearches.filter((item) => item !== search);
    setRecentSearches(updatedSearches);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updatedSearches));
  };

  const handleSelectSearch = (search: string) => {
    setSearchQuery(search);
    setShowDropdown(false);
    onSearch(search);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();

      const query = searchQuery.trim();

      // Only proceed if query is not empty
      if (!query) {
        // Optionally clear results or show message
        onSearch("");
        setShowDropdown(false);
        return;
      }

      // If search is on click mode, trigger the search
      if (searchOn === "click") {
        handleSearchOnClick();
      } else {
        // For typing mode, trigger search directly
        onSearch(query);
        setShowDropdown(false);
      }
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        className={className}
        onSubmit={(e) => {
          e.preventDefault();
          handleSearchOnClick();
        }}
      >
        <div className="relative h-full w-full" ref={dropdownRef}>
          <GeneralInput
            onFocus={() => setShowDropdown(true)}
            customStyleClasses={`!ps-8 main_warper h-full ${inputStyle}`}
            name="search"
            maxLength={40}
            placeholder={placeholder ?? t("search")}
            onChange={handleChange}
            onButtonClick={() => {}}
            onKeyDown={handleKeyDown}
          />
          <button
            type="submit"
            className="absolute inset-y-0 start-0 flex items-center px-3 hover:cursor-pointer"
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader />
            ) : (
              <img
                className="h-3 w-3"
                src={ic_search}
                alt={t("search") || "Search"}
              />
            )}
          </button>

          {showDropdown && showSearchHistory && recentSearches.length > 0 && (
            <ul className="absolute z-10 mt-1 w-full rounded-lg border border-primary-bg bg-white p-3 shadow-sm">
              <p className="border-b border-primary-bg pb-2 text-body-text">
                {t("recent_searches")}
              </p>
              {recentSearches.map((search, index) => (
                <li
                  onClick={() => handleSelectSearch(search)}
                  key={index}
                  className="flex cursor-pointer items-center justify-between rounded-lg px-2 py-1 hover:bg-primary-bg"
                >
                  <span>{search}</span>
                  <button
                    type="button"
                    onClick={() => removeSearch(search)}
                    className="text-2xl text-body-text hover:text-primary"
                  >
                    &times;
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </form>
    </FormProvider>
  );
};

export default SearchComponent;
